/* eslint-disable max-lines */
import { api } from "jiffy-api";
import { track, trackRudderstack } from "jiffy-analytics";

import EventBus from "common/eventbus";
import Events from "common/events";
import { redirectTo, isBare, isReplaceShirtPage } from "common/utils/browser";
import EventClass from "common/utils/ui/event-class";
import shouldShowSampleLimitModal from "common/utils/ui/sample-limit-checker";
import { waitFor } from "common/utils/ui/wait_for";

import { showSampleLimitModal } from "../product-details-sample-order-limit-modal/product-details-sample-order-limit-modal";
import "./product-details-upload-button.scss";

class ProductDetailsUploadButton extends EventClass {
  #frontUploadSelector = "#field-artwork-upload-front";

  #wrapperSelector = ".js-product-details-upload-button-wrapper";

  #buyButtonSelector = "#js-product-add-to-cart-sticky-container";

  #artworkExists = false;

  #inputVisible = false;

  #productDetailsQuantityFormSelector = ".js-product-details-quantity-form";

  #activeColorVariantSelectorSelector = ".js-color-variant-selector-active";

  #fileInputSelector = ".js-design-file-input";

  #linkSelector = ".js-start-designing";

  get $wrapper() {
    return document.querySelector(this.#wrapperSelector);
  }

  get $fileInput() {
    return this.$wrapper?.querySelector(this.#fileInputSelector);
  }

  get $buyButton() {
    return document.querySelector(this.#buyButtonSelector);
  }

  get $frontInput() {
    return document.querySelector(this.#frontUploadSelector);
  }

  get $productQuantityForm() {
    return document.querySelector(this.#productDetailsQuantityFormSelector);
  }

  get $link() {
    return this.$wrapper?.querySelector(this.#linkSelector);
  }

  get isDesignLab() {
    return this.$wrapper?.dataset.designLab === "true";
  }

  get $activeColorVariantSelector() {
    return document.querySelector(this.#activeColorVariantSelectorSelector);
  }

  get catalogNumber() {
    return this.$wrapper?.dataset.catalogNumber;
  }

  get color() {
    return this.$activeColorVariantSelector?.dataset.color;
  }

  get isSample() {
    return this.$wrapper?.dataset.sample === "true";
  }

  get $progressElement() {
    return this.progressElement;
  }

  toggleButtonsVisibility() {
    const uploadButtonShown = !this.#inputVisible && !this.#artworkExists;
    this.$wrapper.classList.toggle("hidden", !uploadButtonShown);
    this.$buyButton?.classList.toggle("force-unsticky", !this.#artworkExists);
  }

  checkInputVisibility() {
    if (!this.$frontInput) {
      this.#inputVisible = false;
    } else {
      const { top: appTop } = this.$frontInput.getBoundingClientRect();
      this.#inputVisible = appTop < window.innerHeight;
    }
  }

  event(e) {
    super.event(e);

    if (this.isSample) {
      this.handleSampleFlow();
      return;
    }

    this.continueFlow();
  }

  handleSampleFlow() {
    // If dynamic data isn't loaded yet, wait for it
    if (!window.document.pdpDynamicData) {
      waitFor(
        "pdpDynamicData",
        () => window.document.pdpDynamicData,
        () => {
          // Data loaded, check sample limits and continue
          if (shouldShowSampleLimitModal(this.isSample)) {
            showSampleLimitModal();
          } else {
            this.continueFlow();
          }
        }
      );
      return;
    }

    // Check if we should show the sample limit modal
    if (shouldShowSampleLimitModal(this.isSample)) {
      showSampleLimitModal();
      return;
    }

    this.continueFlow();
  }

  continueFlow() {
    if (!this.isDesignLab) {
      this.handleNonDesignLabFlow();
    } else {
      this.handleDesignLabFlow();
    }
  }

  handleNonDesignLabFlow() {
    document.querySelector(this.#frontUploadSelector).click();
  }

  handleDesignLabFlow() {
    if (isBare()) {
      const { color, compositionId } = this.$activeColorVariantSelector?.dataset || {};
      const catalogId = this.catalogNumber;

      window.parent.postMessage(
        {
          type: "designlabmodal:productadded",
          payload: {
            catalogId,
            compositionId,
            colorName: color,
            replace: isReplaceShirtPage()
          }
        },
        window.location.origin
      );
    } else {
      // Design Lab, but not bare page
      this.trackStartDesigning();

      if (this.$fileInput) {
        // Trigger file input if available
        this.$fileInput.click();
      } else {
        this.$productQuantityForm.submit();
      }
    }
  }

  handleFileSelect(e) {
    const { files } = e.target;
    if (files && files.length > 0) {
      this.processFile(files[0]);
    }
    // Reset input
    e.target.value = "";
  }

  processFile(file) {
    this.showUploadProgress(file.name);
    this.uploadFile(file);
  }

  showUploadProgress(fileName) {
    // Hide the button instead of replacing content
    const button = this.$wrapper.querySelector(".js-product-details-upload-button");
    if (button) {
      button.classList.add("hidden");
    }

    // Create and show progress element
    this.progressElement = document.createElement("div");
    this.progressElement.className = "text-center py-4";
    this.progressElement.innerHTML = `
      <p class="js-upload-filename text-lg font-semibold mb-2">Uploading...</p>
      <div class="w-full bg-gray-200 rounded-full h-2">
        <div class="js-progress-bar bg-blue-600 h-2 rounded-full transition-all duration-300" style="width: 0%"></div>
      </div>
    `;

    // Safely set the filename using textContent
    const filenameElement = this.progressElement.querySelector(".js-upload-filename");
    filenameElement.textContent = `Uploading ${fileName}...`;

    this.$wrapper.appendChild(this.$progressElement);
    this.$wrapper.classList.remove("hidden");
  }

  updateProgress(percentage) {
    const progressBar = this.$progressElement?.querySelector(".js-progress-bar");
    if (progressBar) {
      progressBar.style.width = `${percentage}%`;
    }
  }

  showError(message) {
    // Remove progress element
    if (this.$progressElement) {
      this.$progressElement.remove();
      this.progressElement = null;
    }

    // Restore button visibility
    const button = this.$wrapper.querySelector(".js-product-details-upload-button");
    if (button) {
      button.classList.remove("hidden");
    }

    const errorDiv = document.createElement("div");
    errorDiv.className =
      "bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-center";
    errorDiv.textContent = message;

    this.$wrapper.insertAdjacentElement("beforebegin", errorDiv);

    setTimeout(() => {
      errorDiv.remove();
    }, 5000);
  }

  async uploadFile(file) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("generate_link", true);
    formData.append("is_made", true);
    formData.append("is_made_flash", false);

    if (this.catalogNumber) {
      formData.append("catalog_number", this.catalogNumber);
    }
    if (this.color) {
      formData.append("ac", this.color);
    }
    if (this.isSample) {
      formData.append("sample", "true");
    }

    try {
      const response = await api.dtf.uploadArtwork(formData, {
        raw: true,
        headers: { "Content-Type": undefined },
        useAxiosForRequest: true,
        onUploadProgress: ({ progress }) => {
          this.updateProgress(progress * 100);
        }
      });

      if (response.redirect_url) {
        redirectTo(response.redirect_url);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || "Upload failed. Please try again.";
      this.showError(errorMessage);
    }
  }

  trackStartDesigning() {
    const compositionName = this.$activeColorVariantSelector?.dataset.color;
    track("start_designing_clicked", {
      catalog_number: this.catalogNumber,
      composition_name: compositionName,
      source: "upload"
    });

    trackRudderstack(
      "CTA Clicked",
      {
        cta_type: "start_designing_clicked",
        type: "button",
        text: "Start Designing",
        destination: "Design Lab",
        source: "upload",
        trigger_location: "mobile_upload_button",
        catalog_number: this.catalogNumber,
        composition_name: compositionName
      },
      true
    );
  }

  // eslint-disable-next-line max-lines-per-function
  init() {
    if (!this.$wrapper) return;

    // Add file input event listener
    if (this.$fileInput) {
      this.$fileInput.addEventListener("change", this.handleFileSelect.bind(this));
    }

    if (isReplaceShirtPage()) {
      const buttonText = this.$wrapper.querySelector(".js-product-details-upload-button-text");
      if (buttonText) {
        buttonText.textContent = "Select Product";
      }
    }

    document.addEventListener("scroll", () => {
      this.checkInputVisibility();
      this.toggleButtonsVisibility();
    });

    EventBus.on(Events.PRODUCT_COLOR_SELECTED, async (_, { color, compositionId }) => {
      if (this.$link) {
        this.$link.dataset.color = color;
        this.$link.dataset.compositionId = compositionId;
      }
    });
  }
}

export default () =>
  new ProductDetailsUploadButton({
    eventSelector: ".js-product-details-upload-button"
  });
